import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { PermissionService } from '../../permissions/permission.service';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  CrmFeature,
  LeadStatusesService,
  LeadStatusDetailDto,
  LeadStatusDto,
  LeadStatusTransitionDto,
} from '../../../shared/services/lead-statuses.service';
import {
  AdminButtonComponent,
  AdminEmptyStateComponent,
  AdminModalComponent,
  AdminPageHeaderComponent,
  StatusBadgeComponent,
} from '../../../shared/admin';

type TransitionForm = {
  targetStatusId: string;
  transitionName: string;
  description: string;
  requiresComment: boolean;
};

@Component({
  selector: 'app-lead-status-transitions-page',
  standalone: true,
  imports: [
    FormsModule,
    RouterLink,
    AdminPageHeaderComponent,
    AdminButtonComponent,
    AdminEmptyStateComponent,
    AdminModalComponent,
    StatusBadgeComponent,
  ],
  template: `
    <section>
      <app-admin-page-header>
        <ng-container actions>
          <app-admin-button variant="secondary" type="button" [routerLink]="listRoute">
            Back to list
          </app-admin-button>
          @if (showManageTransitions()) {
            <app-admin-button variant="primary" type="button" (click)="openAdd()">Add Transition</app-admin-button>
          }
        </ng-container>
      </app-admin-page-header>

      <div class="cg-admin-card" style="padding: var(--cg-space-5)">
        <h2 class="text-sm font-semibold text-slate-700 mb-4">Allowed Transitions</h2>

        @if (transitions().length === 0) {
          <app-admin-empty-state icon="inbox" title="No transitions configured" />
        } @else {
          <ul class="space-y-4">
            @for (t of transitions(); track t._id) {
              <li class="flex flex-wrap items-center gap-3 border-b border-slate-100 pb-4 last:border-0">
                <div class="flex flex-col items-start gap-1 min-w-[12rem]">
                  <span class="font-semibold text-slate-900">{{ t.transitionName }}</span>
                  <span class="text-sm text-slate-500">
                    {{ status()?.name }} → {{ targetName(t) }}
                  </span>
                </div>
                <div class="flex flex-wrap items-center gap-2">
                  @if (t.requiresComment) {
                    <app-status-badge variant="draft">Comment required</app-status-badge>
                  }
                  @if (showManageTransitions()) {
                    <app-admin-button variant="ghost" type="button" (click)="openEdit(t)">Edit</app-admin-button>
                    <app-admin-button variant="danger" type="button" (click)="remove(t)">Remove</app-admin-button>
                  }
                </div>
              </li>
            }
          </ul>
        }
      </div>

      @if (showForm()) {
        <app-admin-modal [title]="editingId() ? 'Edit transition' : 'Add transition'" size="lg" (closed)="closeForm()">
          <div class="cg-admin-form-grid cg-admin-form-grid--2">
            <label class="block text-sm sm:col-span-2">
              <span class="text-slate-700">From Status</span>
              <input class="mt-1 w-full rounded border p-2 bg-slate-50" [value]="status()?.name ?? ''" disabled />
            </label>
            @if (!editingId()) {
              <label class="block text-sm sm:col-span-2">
                <span class="text-slate-700">To Status *</span>
                <select class="mt-1 w-full rounded border p-2" [(ngModel)]="form.targetStatusId" name="targetStatusId">
                  <option value="">Select status</option>
                  @for (s of availableTargets(); track s._id) {
                    <option [value]="s._id">{{ s.name }} ({{ s.code }})</option>
                  }
                </select>
              </label>
            } @else {
              <label class="block text-sm sm:col-span-2">
                <span class="text-slate-700">To Status</span>
                <input class="mt-1 w-full rounded border p-2 bg-slate-50" [value]="editTargetName()" disabled />
              </label>
            }
            <label class="block text-sm sm:col-span-2">
              <span class="text-slate-700">Transition Name *</span>
              <input
                class="mt-1 w-full rounded border p-2"
                [(ngModel)]="form.transitionName"
                name="transitionName"
                maxlength="100"
                placeholder="e.g. Application Started"
              />
            </label>
            <label class="block text-sm sm:col-span-2">
              <span class="text-slate-700">Description</span>
              <textarea
                class="mt-1 w-full rounded border p-2"
                rows="2"
                [(ngModel)]="form.description"
                name="description"
                placeholder="Optional explanation for administrators"
              ></textarea>
            </label>
            <label class="block text-sm sm:col-span-2 flex items-center gap-2">
              <input type="checkbox" [(ngModel)]="form.requiresComment" name="requiresComment" />
              <span class="text-slate-700">Requires comment</span>
            </label>
          </div>
          @if (error(); as msg) {
            <p class="mt-3 text-sm text-red-600">{{ msg }}</p>
          }
          <div class="cg-admin-page-header__actions" style="margin-top: var(--cg-space-5); justify-content: flex-end">
            <app-admin-button variant="cancel" type="button" (click)="closeForm()">Cancel</app-admin-button>
            @if (showManageTransitions()) {
              <app-admin-button variant="accent" type="button" (click)="saveForm()" [disabled]="saving()">
                {{ saving() ? 'Saving...' : 'Save' }}
              </app-admin-button>
            }
          </div>
        </app-admin-modal>
      }
    </section>
  `,
})
export class LeadStatusTransitionsPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(LeadStatusesService);
  private readonly permissions = inject(PermissionService);

  readonly crmFeatures: CrmFeature = this.route.snapshot.data['crmFeatures'] ?? 'leads';

  private statusModuleKey(): string {
    return this.crmFeatures === 'applicants' ? 'crm.applicantStatuses' : 'crm.leadStatuses';
  }

  readonly showManageTransitions = computed(() =>
    this.permissions.canShow(this.statusModuleKey(), 'manageTransitions'),
  );
  readonly canManageTransitions = computed(() =>
    this.permissions.canExecute(this.statusModuleKey(), 'manageTransitions'),
  );
  readonly listRoute =
    this.crmFeatures === 'applicants' ? '/platform/crm/applicant-statuses' : '/platform/crm/lead-statuses';

  readonly status = signal<LeadStatusDetailDto | null>(null);
  readonly allStatuses = signal<LeadStatusDto[]>([]);
  readonly transitions = signal<LeadStatusTransitionDto[]>([]);
  readonly showForm = signal(false);
  readonly editingId = signal<string | null>(null);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);

  statusId = '';
  form: TransitionForm = this.emptyForm();

  ngOnInit(): void {
    this.statusId = this.route.snapshot.paramMap.get('id') ?? '';
    this.api.list(this.crmFeatures).subscribe((items) => this.allStatuses.set(items));
    this.refresh();
  }

  private emptyForm(): TransitionForm {
    return {
      targetStatusId: '',
      transitionName: '',
      description: '',
      requiresComment: false,
    };
  }

  refresh(): void {
    this.api.getById(this.statusId, this.crmFeatures).subscribe((detail) => {
      this.status.set(detail);
      this.transitions.set(detail.transitions ?? []);
    });
  }

  targetName(t: LeadStatusTransitionDto): string {
    const to = t.toStatusId;
    if (typeof to === 'object' && to !== null && 'name' in to) {
      return to.name;
    }
    const match = this.allStatuses().find((s) => s._id === String(to));
    return match?.name ?? String(to);
  }

  editTargetName(): string {
    const id = this.editingId();
    const t = this.transitions().find((x) => x._id === id);
    return t ? this.targetName(t) : '';
  }

  availableTargets(): LeadStatusDto[] {
    const configured = new Set(
      this.transitions().map((t) =>
        typeof t.toStatusId === 'object' ? t.toStatusId._id : String(t.toStatusId),
      ),
    );
    return this.allStatuses().filter(
      (s) => s._id !== this.statusId && s.isActive && !configured.has(s._id),
    );
  }

  openAdd(): void {
    this.form = this.emptyForm();
    this.editingId.set(null);
    this.error.set(null);
    this.showForm.set(true);
  }

  openEdit(t: LeadStatusTransitionDto): void {
    this.form = {
      targetStatusId: typeof t.toStatusId === 'object' ? t.toStatusId._id : String(t.toStatusId),
      transitionName: t.transitionName,
      description: t.description ?? '',
      requiresComment: t.requiresComment,
    };
    this.editingId.set(t._id);
    this.error.set(null);
    this.showForm.set(true);
  }

  closeForm(): void {
    this.showForm.set(false);
  }

  saveForm(): void {
    if (!this.form.transitionName.trim()) {
      this.error.set('Transition name is required.');
      return;
    }

    const id = this.editingId();
    if (!id && !this.form.targetStatusId) {
      this.error.set('Select a target status.');
      return;
    }
    if (!id && this.form.targetStatusId === this.statusId) {
      this.error.set('Cannot transition to the same status.');
      return;
    }

    this.saving.set(true);
    this.error.set(null);

    const payload = {
      transitionName: this.form.transitionName.trim(),
      description: this.form.description.trim() || undefined,
      requiresComment: this.form.requiresComment,
    };

    const req$ = id
      ? this.api.updateTransition(id, payload)
      : this.api.addTransition({
          fromStatusId: this.statusId,
          toStatusId: this.form.targetStatusId,
          ...payload,
        });

    req$.subscribe({
      next: () => {
        this.saving.set(false);
        this.showForm.set(false);
        this.refresh();
      },
      error: (err) => {
        this.saving.set(false);
        this.error.set(err?.error?.message ?? 'Save failed.');
      },
    });
  }

  remove(t: LeadStatusTransitionDto): void {
    if (!confirm(`Remove transition "${t.transitionName}"?`)) return;
    this.api.removeTransition(t._id).subscribe(() => this.refresh());
  }
}
